<template>
  <div class="space-y-2">
    <!-- Added entries as removable chips. -->
    <div v-if="modelValue.length" class="flex flex-wrap gap-2">
      <Chip
        v-for="(item, i) in modelValue"
        :key="`${item}-${i}`"
        data-test="wizard-list-chip"
        :remove-label="t('onboarding.actions.remove')"
        @remove="removeAt(i)"
      >
        <span class="truncate max-w-[16rem]">{{ item }}</span>
      </Chip>
    </div>
    <p v-else-if="emptyText" class="text-sm text-tertiary">{{ emptyText }}</p>

    <!-- Input + ➕ to add one entry (Enter also adds). -->
    <div class="flex gap-2">
      <Input v-model="draft" data-test="wizard-list-input" :placeholder="placeholder" @keyup.enter="add" />
      <Button
        data-test="wizard-list-add"
        variant="secondary"
        :disabled="!draft.trim()"
        :aria-label="t('onboarding.actions.add')"
        @click="add"
      >
        <LumiIcon name="add" class="h-4 w-4" />
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Chip, Input, LumiIcon } from '@lumi/ui';

// Shared add-via-➕ list editor for the onboarding wizard (issue 214). Replaces the
// comma-separated inputs and the org-structure "Hinzufügen" button — same pattern
// everywhere: type an entry, press Enter or ➕, remove via the chip's ✕. Operates
// on a plain string[] so every list field (org units, salutations, employment
// vocabularies, apprenticeship professions) stages a clean array.
const props = defineProps<{ modelValue: string[]; placeholder?: string; emptyText?: string }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: string[]): void }>();

const { t } = useI18n();
const draft = ref('');

function add() {
  const value = draft.value.trim();
  if (!value) return;
  // Case-insensitive de-dupe: adding an existing entry just clears the input.
  if (props.modelValue.some((v) => v.toLowerCase() === value.toLowerCase())) {
    draft.value = '';
    return;
  }
  emit('update:modelValue', [...props.modelValue, value]);
  draft.value = '';
}

function removeAt(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

// The section calls this when the user leaves it: an entry typed but never
// added with Enter / ➕ is kept, not silently dropped (#546).
defineExpose({ flush: add });
</script>
